import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, BehaviorSubject, of, timer, from } from 'rxjs';
import { tap, shareReplay, switchMap } from 'rxjs/operators';
import { CachingService } from '@core/services/infra/caching.service';
import { UnifiedLoadingCoordinatorService } from '@core/services/infra/unified-loading-coordinator.service';

export interface CacheStrategy {
  ttl: number;
  storage: 'memory' | 'browser-cache' | 'indexed-db' | 'local-storage';
  versioned?: boolean;
  backgroundRefresh?: boolean;
  tags?: string[];
}

export interface DataFreshness {
  lastUpdated: Date;
  isStale: boolean;
  stalenessScore: number; // 0-1, where 1 is completely stale
}

export interface CacheLayer {
  name: string;
  priority: number;
  storage: Storage | Cache | IDBDatabase;
  maxSize: number;
  ttl: number;
}

/**
 * @domain Core/Services
 * 
 * Enhanced Caching Service - Multi-layer caching with progressive loading support
 * 
 * This service provides:
 * - Multi-layer caching (memory, browser cache, IndexedDB)
 * - Cache-aware progressive loading
 * - Background refresh strategies
 * - Version-aware cache invalidation
 * - Smart cache warming for returning users
 * - Offline-first caching patterns
 */
@Injectable({
  providedIn: 'root'
})
export class EnhancedCachingService extends CachingService {
  
  // Cache strategies for different data types
  private readonly cacheStrategies: Record<string, CacheStrategy> = {
    // Static assets - cache forever with versioning
    'static-assets': {
      ttl: Infinity,
      storage: 'browser-cache',
      versioned: true
    },
    
    // Application code - cache until version changes
    'application-code': {
      ttl: 24 * 60 * 60 * 1000, // 24 hours
      storage: 'browser-cache',
      versioned: true
    },
    
    // User profile data - memory cache with background refresh
    'user-data': {
      ttl: 15 * 60 * 1000, // 15 minutes
      storage: 'memory',
      backgroundRefresh: true,
      tags: ['user', 'profile']
    },
    
    // Property data - IndexedDB with background refresh
    'property-data': {
      ttl: 30 * 60 * 1000, // 30 minutes
      storage: 'indexed-db',
      backgroundRefresh: true,
      tags: ['properties', 'data']
    },
    
    // Navigation and UI state - local storage
    'ui-state': {
      ttl: 7 * 24 * 60 * 60 * 1000, // 7 days
      storage: 'local-storage',
      tags: ['ui', 'navigation']
    },
    
    // Financial data - IndexedDB with shorter TTL
    'financial-data': {
      ttl: 10 * 60 * 1000, // 10 minutes
      storage: 'indexed-db',
      backgroundRefresh: true,
      tags: ['financial', 'data']
    }
  };

  // Cache layers in priority order (higher priority = checked first)
  private cacheLayers: CacheLayer[] = [];
  
  // Background refresh queue
  private refreshQueue = new Set<string>();
  private isRefreshing = false;

  // Cache warming status
  private warmingSubject = new BehaviorSubject<boolean>(false);
  public isWarming$ = this.warmingSubject.asObservable();

  private readonly enhancedDestroyRef = inject(DestroyRef);

  constructor(
    private loadingCoordinator?: UnifiedLoadingCoordinatorService
  ) {
    super();
    void this.initializeCacheLayers();
    this.startBackgroundRefreshWorker();
  }

  /**
   * Initialize cache layers
   */
  private async initializeCacheLayers(): Promise<void> {
    console.log('🔧 EnhancedCaching: Initializing cache layers');

    // Initialize IndexedDB
    await this.initializeIndexedDB();
    
    // Initialize browser cache
    if ('caches' in window) {
      await this.initializeBrowserCache();
    }

  }

  /**
   * Initialize IndexedDB for large data caching
   */
  private async initializeIndexedDB(): Promise<void> {
    return new Promise((resolve, _reject) => {
      const request = indexedDB.open('ProporioCache', 1);
      
      request.onerror = () => {
        console.warn('⚠️ IndexedDB initialization failed');
        resolve(); // Don't fail completely
      };
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object stores
        if (!db.objectStoreNames.contains('cache-entries')) {
          const store = db.createObjectStore('cache-entries', { keyPath: 'key' });
          store.createIndex('tags', 'tags', { multiEntry: true });
          store.createIndex('expiry', 'expiry');
        }
      };
    });
  }

  /**
   * Initialize browser cache
   */
  private async initializeBrowserCache(): Promise<void> {
    try {
      await caches.open('proporio-app-v1');
      await caches.open('proporio-data-v1');
      await caches.open('proporio-static-v1');
    } catch (error) {
      console.warn('⚠️ Browser cache initialization failed:', error);
    }
  }

  /**
   * Enhanced cache set with strategy-based storage
   */
  setWithStrategy<T>(
    key: string, 
    value: T, 
    strategyName: string,
    customTTL?: number
  ): Promise<void> {
    const strategy = this.cacheStrategies[strategyName];
    if (!strategy) {
      console.warn(`⚠️ Unknown cache strategy: ${strategyName}`);
      return Promise.resolve();
    }

    const ttl = customTTL || strategy.ttl;
    const expiry = ttl === Infinity ? Infinity : Date.now() + ttl;

    switch (strategy.storage) {
      case 'memory':
        this.set(key, value, ttl, strategy.tags);
        return Promise.resolve();
        
      case 'local-storage':
        return this.setInLocalStorage(key, value, expiry, strategy.tags);
        
      case 'indexed-db':
        return this.setInIndexedDB(key, value, expiry, strategy.tags);
        
      case 'browser-cache':
        return this.setInBrowserCache(key, value, expiry);
        
      default:
        return Promise.resolve();
    }
  }

  /**
   * Enhanced cache get with multi-layer lookup
   */
  async getWithStrategy<T>(key: string, strategyName: string): Promise<T | null> {
    const strategy = this.cacheStrategies[strategyName];
    if (!strategy) {
      return null;
    }

    // Try memory first (fastest)
    const memoryValue = this.get<T>(key);
    if (memoryValue !== null) {
      console.log(`🎯 Memory cache HIT: ${key}`);
      return memoryValue;
    }

    // Try strategy-specific storage
    let value: T | null = null;
    
    switch (strategy.storage) {
      case 'local-storage':
        value = await this.getFromLocalStorage<T>(key);
        break;
        
      case 'indexed-db':
        value = await this.getFromIndexedDB<T>(key);
        break;
        
      case 'browser-cache':
        value = await this.getFromBrowserCache<T>(key);
        break;
    }

    if (value !== null) {
      // Promote to memory cache for faster access
      this.set(key, value, strategy.ttl, strategy.tags);
      
      // Schedule background refresh if needed
      if (strategy.backgroundRefresh) {
        this.scheduleBackgroundRefresh(key, strategyName);
      }
      
      console.log(`📦 ${strategy.storage} cache HIT: ${key}`);
      return value;
    }

    return null;
  }

  /**
   * Cache observable with enhanced strategy
   */
  cacheObservableWithStrategy<T>(
    key: string,
    source: Observable<T>,
    strategyName: string,
    customTTL?: number
  ): Observable<T> {
    return from(this.getWithStrategy<T>(key, strategyName)).pipe(
      switchMap(cached => {
        if (cached !== null) {
          return of(cached);
        }
        
        // Cache miss - fetch from source
        return source.pipe(
          tap(data => {
            void this.setWithStrategy(key, data, strategyName, customTTL);
          }),
          shareReplay(1)
        );
      })
    );
  }

  /**
   * Warm cache for returning users
   */
  async warmCacheForUser(userUID: string): Promise<void> {
    console.log(`🔥 Warming cache for user: ${userUID}`);
    this.warmingSubject.next(true);

    try {
      const warmingTasks = [
        this.warmUserData(userUID),
        this.warmRecentProperties(userUID),
        this.warmNavigationState(userUID),
        this.warmDashboardData(userUID)
      ];

      await Promise.allSettled(warmingTasks);
      
    } catch (error) {
      console.warn('⚠️ Cache warming failed:', error);
    } finally {
      this.warmingSubject.next(false);
    }
  }

  /**
   * Check data freshness
   */
  async checkDataFreshness(key: string): Promise<DataFreshness> {
    const entry = await this.getCacheEntry(key);
    
    if (!entry) {
      return {
        lastUpdated: new Date(0),
        isStale: true,
        stalenessScore: 1
      };
    }

    const age = Date.now() - entry.timestamp;
    const maxAge = entry.ttl || 300000; // 5 minutes default
    const stalenessScore = Math.min(age / maxAge, 1);

    return {
      lastUpdated: new Date(entry.timestamp),
      isStale: stalenessScore > 0.8, // Consider stale if 80% of TTL passed
      stalenessScore
    };
  }

  /**
   * Handle app version changes
   */
  async handleAppVersionChange(newVersion: string): Promise<void> {
    const currentVersion = await this.getFromLocalStorage<string>('app-version');
    
    if (currentVersion !== newVersion) {
      console.log(`🔄 App version changed: ${currentVersion} → ${newVersion}`);
      
      // Clear versioned caches
      await this.invalidateVersionedCaches();
      
      // Update version
      await this.setInLocalStorage('app-version', newVersion, Infinity);
      
      // Preload new version components
      this.preloadNewVersionComponents();
    }
  }

  // Private helper methods

  private async setInLocalStorage<T>(
    key: string, 
    value: T, 
    expiry: number, 
    tags?: string[]
  ): Promise<void> {
    try {
      const entry = {
        value,
        expiry,
        tags: tags || [],
        timestamp: Date.now()
      };
      localStorage.setItem(`cache:${key}`, JSON.stringify(entry));
    } catch (error) {
      console.warn(`⚠️ LocalStorage set failed for ${key}:`, error);
    }
  }

  private async getFromLocalStorage<T>(key: string): Promise<T | null> {
    try {
      const item = localStorage.getItem(`cache:${key}`);
      if (!item) return null;
      
      const entry = JSON.parse(item);
      
      // Check expiry
      if (entry.expiry !== Infinity && Date.now() > entry.expiry) {
        localStorage.removeItem(`cache:${key}`);
        return null;
      }
      
      return entry.value;
    } catch (error) {
      console.warn(`⚠️ LocalStorage get failed for ${key}:`, error);
      return null;
    }
  }

  private async setInIndexedDB<T>(
    key: string, 
    value: T, 
    expiry: number, 
    tags?: string[]
  ): Promise<void> {
    try {
      const db = await this.openIndexedDB();
      const transaction = db.transaction(['cache-entries'], 'readwrite');
      const store = transaction.objectStore('cache-entries');
      
      const entry = {
        key,
        value,
        expiry,
        tags: tags || [],
        timestamp: Date.now()
      };
      
      await store.put(entry);
    } catch (error) {
      console.warn(`⚠️ IndexedDB set failed for ${key}:`, error);
    }
  }

  private async getFromIndexedDB<T>(key: string): Promise<T | null> {
    try {
      const db = await this.openIndexedDB();
      const transaction = db.transaction(['cache-entries'], 'readonly');
      const store = transaction.objectStore('cache-entries');
      
      return new Promise((resolve) => {
        const request = store.get(key);
        
        request.onsuccess = () => {
          const entry = request.result;
          
          if (!entry) {
            resolve(null);
            return;
          }
          
          // Check expiry
          if (entry.expiry !== Infinity && Date.now() > entry.expiry) {
            // Clean up expired entry
            const deleteTransaction = db.transaction(['cache-entries'], 'readwrite');
            deleteTransaction.objectStore('cache-entries').delete(key);
            resolve(null);
            return;
          }
          
          resolve(entry.value);
        };
        
        request.onerror = () => resolve(null);
      });
    } catch (error) {
      console.warn(`⚠️ IndexedDB get failed for ${key}:`, error);
      return null;
    }
  }

  private async setInBrowserCache<T>(
    key: string, 
    value: T, 
    expiry: number
  ): Promise<void> {
    if (!('caches' in window)) return;
    
    try {
      const cache = await caches.open('proporio-data-v1');
      const response = new Response(JSON.stringify({
        value,
        expiry,
        timestamp: Date.now()
      }));
      
      await cache.put(`/cache/${key}`, response);
    } catch (error) {
      console.warn(`⚠️ Browser cache set failed for ${key}:`, error);
    }
  }

  private async getFromBrowserCache<T>(key: string): Promise<T | null> {
    if (!('caches' in window)) return null;
    
    try {
      const cache = await caches.open('proporio-data-v1');
      const response = await cache.match(`/cache/${key}`);
      
      if (!response) return null;
      
      const entry = await response.json();
      
      // Check expiry
      if (entry.expiry !== Infinity && Date.now() > entry.expiry) {
        await cache.delete(`/cache/${key}`);
        return null;
      }
      
      return entry.value;
    } catch (error) {
      console.warn(`⚠️ Browser cache get failed for ${key}:`, error);
      return null;
    }
  }

  private async openIndexedDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('ProporioCache', 1);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private scheduleBackgroundRefresh(key: string, strategyName: string): void {
    this.refreshQueue.add(`${key}:${strategyName}`);
    
    if (!this.isRefreshing) {
      void this.processRefreshQueue();
    }
  }

  private async processRefreshQueue(): Promise<void> {
    if (this.refreshQueue.size === 0) return;
    
    this.isRefreshing = true;
    
    // Process refresh queue (implementation depends on your data sources)
    // This would typically involve re-fetching data from your APIs
    
    this.refreshQueue.clear();
    this.isRefreshing = false;
  }

  private startBackgroundRefreshWorker(): void {
    // Process refresh queue every 30 seconds, but only if allowed by loading coordinator
    // (app-lifetime interval; belt for the leak checker)
    timer(0, 30000).pipe(takeUntilDestroyed(this.enhancedDestroyRef)).subscribe(() => {
      // Check if background refresh is allowed
      const canRefresh = this.loadingCoordinator?.canRefreshBackground() ?? true;
      
      if (canRefresh && this.refreshQueue.size > 0) {
        console.log('🔄 Background refresh allowed, processing queue');
        void this.processRefreshQueue();
      } else if (!canRefresh) {
        console.log('🚫 Background refresh blocked by loading coordinator');
      }
    });
  }

  // Cache warming methods
  private async warmUserData(userUID: string): Promise<void> {
    // Implementation would fetch and cache user data
    console.log(`🔥 Warming user data for ${userUID}`);
  }

  private async warmRecentProperties(userUID: string): Promise<void> {
    // Implementation would fetch and cache recent properties
    console.log(`🔥 Warming recent properties for ${userUID}`);
  }

  private async warmNavigationState(userUID: string): Promise<void> {
    // Implementation would cache navigation preferences
    console.log(`🔥 Warming navigation state for ${userUID}`);
  }

  private async warmDashboardData(userUID: string): Promise<void> {
    // Implementation would cache dashboard data
    console.log(`🔥 Warming dashboard data for ${userUID}`);
  }

  private async invalidateVersionedCaches(): Promise<void> {
    // Clear application and static caches
    await this.invalidateByTag('application');
    await this.invalidateByTag('static');
    
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames
          .filter(name => name.includes('proporio'))
          .map(name => caches.delete(name))
      );
    }
  }

  private preloadNewVersionComponents(): void {
    // This would trigger preloading of new version components
    console.log('🔄 Preloading new version components');
  }

  private async getCacheEntry(key: string): Promise<CacheEntryMeta | null> {
    // Try to get cache entry metadata from any storage layer
    return (
      (await this.getFromLocalStorage<CacheEntryMeta>(`meta:${key}`)) ||
      (await this.getFromIndexedDB<CacheEntryMeta>(`meta:${key}`)) ||
      null
    );
  }
}

/**
 * Metadata we persist alongside each cache entry so we can compute
 * freshness without re-parsing the payload.
 */
interface CacheEntryMeta {
  timestamp: number;
  ttl?: number;
}
